import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign } from 'lucide-react';
import type { User } from '@/types';
import api from '@/lib/api';
import { getUserColor, getRoleBadgeColor, getDisplayRole, getInitials } from '@/utils/calendarHelpers';
import { ROLE_COLORS } from '@/utils/constants';
import { ProfileModal } from './ProfileModal';

export function FamilyTab() {
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get('/users');
      const data = res.data;
      return data.users || data || [];
    },
    staleTime: 1000 * 5,
    refetchInterval: 5000,
  });

  const [filter, setFilter] = useState<'all' | 'parent' | 'caretaker' | 'family' | 'professional'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const filteredUsers = users.filter((u: User) => filter === 'all' || u.role === filter);

  const getFilterLabel = (f: string) => {
    switch (f) {
      case 'all': return 'All';
      case 'parent': return 'Guardians';
      case 'caretaker': return 'Caretakers';
      case 'family': return 'Family';
      case 'professional': return 'Professionals';
      default: return f;
    }
  };

  return (
    <div data-testid="container-family-tab" className="space-y-6">
      <div className="bg-card rounded-xl border border-slate-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-2">Family</h2>
        <p className="text-gray-400 mb-4">
          View all family members and caretakers.
        </p>

        {/* Filter buttons */}
        <div className="flex flex-wrap gap-2 mb-6">
          {(['all', 'parent', 'caretaker', 'family', 'professional'] as const).map((f) => (
            <button
              key={f}
              data-testid={`clickable-family-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
              }`}
            >
              {getFilterLabel(f)}
            </button>
          ))}
        </div>

        {/* Users grid */}
        {isLoading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-400">No users found.</div>
        ) : (
          <div data-testid="container-family-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredUsers.map((user: User) => (
              <button
                key={user.id}
                data-testid={`clickable-person-card-${user.id}`}
                onClick={() => setSelectedUser(user)}
                className="p-4 bg-slate-800 border border-slate-700 rounded-xl hover:border-slate-600 transition-colors cursor-pointer text-left"
                style={{
                  borderLeftColor: getUserColor(user, undefined as any),
                  borderLeftWidth: (user.color || ROLE_COLORS[user.role]) ? '3px' : undefined,
                }}
              >
                <div className="flex items-center gap-3">
                  {user.emoji ? (
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                      style={{
                        backgroundColor: `${getUserColor(user, '#3b82f6')}30`,
                      }}
                    >
                      {user.emoji}
                    </div>
                  ) : (
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
                      style={{
                        backgroundColor: getUserColor(user, '#3b82f6'),
                      }}
                    >
                      {getInitials(user)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">
                      {user.first_name} {user.last_name}
                    </h3>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${getRoleBadgeColor(user.role)}`}>
                      {user.role === 'professional' && user.other_role_label ? user.other_role_label : getDisplayRole(user.role)}
                    </span>
                  </div>
                </div>
                {user.role === 'caretaker' && user.hourly_wage && (
                  <div className="mt-3 pt-3 border-t border-slate-700 text-sm text-gray-400">
                    <div className="flex items-center gap-1">
                      <DollarSign size={14} />
                      ${user.hourly_wage}/hr
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Profile Modal */}
      {selectedUser && (
        <ProfileModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
}
